// src/Currency/CurrencyContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

const url = process.env.REACT_APP_BACKEND_URL;

export const CurrencyProvider = ({ children, company }) => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ----- 1. FETCH ALL ACTIVE CURRENCIES FROM BACKEND -----
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${url}/api/currency`);

        if (response.data.success) {
          setCurrencies(response.data.data);
          setError(null);
        } else {
          throw new Error(response.data.message || 'Failed to fetch currencies');
        }
      } catch (err) {
        setError(err.message);

        // 👇 Fallback: hardcoded list if API fails (so app still works)
        setCurrencies([
          { code: 'USD', symbol: '$', name: 'US Dollar' },
          { code: 'KES', symbol: 'Ksh', name: 'Kenyan Shilling' },
          { code: 'EUR', symbol: '€', name: 'Euro' },
          { code: 'GBP', symbol: '£', name: 'British Pound' },
          { code: 'SOS', symbol: 'S', name: 'Somali Shilling' },
          { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling' },
          { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrencies();
  }, []);

  // ----- 2. BUILD LOOKUP MAP: code → symbol -----
  const currencyMap = currencies.reduce((acc, cur) => {
    acc[cur.code] = cur.symbol;
    return acc;
  }, {});

  // ----- 3. GET CURRENCY OBJECT FROM COMPANY (handles both 'currency' and 'base_currency') -----
  const getCurrencyObject = () => {
    // Check for 'currency' first
    if (company?.currency && typeof company.currency === 'object') {
      return company.currency;
    }
    // Then check for 'base_currency'
    if (company?.base_currency && typeof company.base_currency === 'object') {
      return company.base_currency;
    }
    return null;
  };

  // ----- 4. GET CURRENCY SYMBOL FOR THE CURRENT COMPANY -----
  const getCurrencySymbol = () => {
    const currencyObj = getCurrencyObject();

    // If we have a currency object with a symbol, use it
    if (currencyObj && currencyObj.symbol) {
      return currencyObj.symbol;
    }

    // If company has a currency code as a string, look it up
    let code = null;
    if (typeof company?.currency === 'string') {
      code = company.currency;
    } else if (company?.currency && typeof company.currency === 'object' && company.currency.code) {
      code = company.currency.code;
    } else if (company?.base_currency && typeof company.base_currency === 'object' && company.base_currency.code) {
      code = company.base_currency.code;
    }

    // Look up the symbol from the database map
    if (code && currencyMap[code]) {
      return currencyMap[code];
    }

    // Fallback to '$'
    return '$';
  };

  // ----- 5. GET CURRENCY CODE (for potential Intl.NumberFormat) -----
  const getCurrencyCode = () => {
    const currencyObj = getCurrencyObject();
    if (currencyObj && currencyObj.code) {
      return currencyObj.code;
    }
    if (typeof company?.currency === 'string') return company.currency;
    if (typeof company?.base_currency === 'string') return company.base_currency;
    return 'USD';
  };

  // ----- 6. FORMAT CURRENCY FUNCTION -----
  const formatCurrency = (amount) => {
    const symbol = getCurrencySymbol();
    if (amount === undefined || amount === null) {
      return `${symbol}0.00`;
    }
    // Simple string concatenation (works with any symbol)
    return `${symbol}${Number(amount).toFixed(2)}`;
  };

  // ----- 7. EXPOSE CONTEXT VALUES -----
  const value = {
    currencies,
    currencyMap,
    loading,
    error,
    getCurrencySymbol,
    getCurrencyCode,
    formatCurrency,
    // Expose the currency object for debugging
    currency: getCurrencyObject(),
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// ----- 8. CUSTOM HOOK FOR USING CURRENCY -----
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;